/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { DeviceModel } from '../../../core/device';
import { BrandingUiInfoKeys } from '../../../core/entities';
import { DefaultServicesClientImpl } from '../../../core/impls';
import { Locale, LocaleKeys } from '../../../core/locale';
import { NetworkManager } from '../../../core/network/NetworkManager';
import type { TivoPayloadAction } from '../../../core/tivo/tivo-redux-wrapper';
import { ListTypes } from '../../../core/utils';
import { CcbSwitch } from '../../../core/utils/ccb';
import { CcbSwitchName } from '../../../core/utils/ccb/enums';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import {
    onKeyDownList,
    onKeyUpList,
    onMouseOutList,
    onMouseOverList,
} from '../../list/list.reducers';
import {
    showGuideScreen,
    showMainSettingsScreen,
    showMenuLanguageScreen,
    showOnepassAndRecordingsMenu,
    showUserPrefAutoplayNextEpisodeScreen,
    showUserPrefVideoWindowScreen,
} from '../../screens/screens.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';
import { openMyShowsOptionsMenu } from './myshowsoptionsmenu/myshowsoptionsmenu.reducers';
import { SET_ONEPASS_AND_RECORDING_OR_MY_SHOWS_OPTIONS_GET_ERROR } from './onepassandrecordings/onepassandrecordings.types';
import {
    IUserPrefSelectedOption,
    setSelectedUserPrefOption,
    setUserPrefOptionsListAction,
} from './userpreferences.actions';
import {
    SET_USER_PREFERENCES_OPTIONS_LIST,
    SET_USER_PREFERENCES_SELECTED_OPTION,
} from './userpreferences.types';

interface IUserPreferences {
    optionsList: Array<IUserPrefSelectedOption>;
    selectedOption: IUserPrefSelectedOption | undefined;
    hasOnePassOrMyShowsGetApiError: boolean;
}

const initialState: IUserPreferences = {
    optionsList: [],
    selectedOption: undefined,
    hasOnePassOrMyShowsGetApiError: false,
};

const userPreferences = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IUserPreferences>>{},
) => {
    switch (type) {
        case SET_USER_PREFERENCES_OPTIONS_LIST:
            return {
                ...state,
                optionsList: payload.optionsList,
                selectedOption: state.selectedOption ?? payload.optionsList[0],
            };
        case SET_USER_PREFERENCES_SELECTED_OPTION:
            return {
                ...state,
                selectedOption: payload.selectedOption,
            };
        case SET_ONEPASS_AND_RECORDING_OR_MY_SHOWS_OPTIONS_GET_ERROR:
            return {
                ...state,
                hasOnePassOrMyShowsGetApiError: payload.hasOnePassOrMyShowsGetApiError,
            };
        default:
            return state;
    }
};

export const setUserPreferencesOptionsList = () => (dispatch: Function) => {
    const displayName = new DefaultServicesClientImpl().getBrandingUiInfoForKey(
        BrandingUiInfoKeys.DISPLAY_NAME,
    );

    const isPlayNextCCBSwitchEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
        CcbSwitchName.ENABLE_PLAY_NEXT_REMOVE_IN_KST_12934,
    );

    const languageMap = {
        en: Locale.getString(LocaleKeys.SETTINGS_ENGLISH),
        es: Locale.getString(LocaleKeys.SETTINGS_SPANISH),
    };
    const selectedVideoWindowOption = DeviceModel.getAppSettings().isVideoWindowEnabled()
        ? Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW_OPTION_YES)
        : Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW_OPTION_NO);
    const selectedAutoPlayOption = DeviceModel.getAppSettings().isAutoplayNextEpisodeEnabled()
        ? Locale.getString(LocaleKeys.SETTINGS_AUTOPLAY_NEXT_EPISODE_ON)
        : Locale.getString(LocaleKeys.SETTINGS_AUTOPLAY_NEXT_EPISODE_OFF);
    const selectedLanguage = languageMap[DeviceModel.getTVProperties().getLang()];
    const settingsItemList: Array<IUserPrefSelectedOption> = [
        {
            title: Locale.getString(LocaleKeys.SETTINGS_MENU_LANGUAGE),
            preview: Locale.getString(LocaleKeys.SETTINGS_MENU_LANGUAGE_PREVIEW_TEXT).replace(
                'DISPLAY_NAME',
                displayName,
            ),
            isDisabled: false,
            value: selectedLanguage,
        },
        {
            title: Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW),
            preview: Locale.getString(
                LocaleKeys.SETTINGS_USER_PREFERENCES_VIDEO_WINDOW_PREVIEW_TEXT,
            ).replace('DISPLAY_NAME', displayName),
            isDisabled: false,
            value: selectedVideoWindowOption,
        },
        {
            title: Locale.getString(LocaleKeys.SETTINGS_AUTOPLAY_NEXT_EPISODE),
            preview: Locale.getString(
                LocaleKeys.SETTINGS_USER_PREFERENCES_AUTOPLAY_NEXT_EPISODE_PREVIEW_TEXT,
            ),
            isDisabled: false,
            value: selectedAutoPlayOption,
        },
        {
            title: Locale.getString(LocaleKeys.SETTINGS_ONEPASS_RECORDING_OPTIONS),
            preview: Locale.getString(LocaleKeys.SETTINGS_ONEPASS_TITLE_PREVIEW_TEXT),
            isDisabled: NetworkManager.isNetworkDisconnected(),
        },
        {
            title: Locale.getString(LocaleKeys.SETTINGS_MY_SHOWS_OPTIONS),
            preview: Locale.getString(LocaleKeys.SETTINGS_MY_SHOWS_OPTIONS_PREVIEW_TEXT),
            isDisabled: NetworkManager.isNetworkDisconnected(),
        },
    ];

    if (!isPlayNextCCBSwitchEnabled) {
        settingsItemList.splice(2, 1);
    }
    dispatch(setUserPrefOptionsListAction(settingsItemList));
};

/**
 * Below reducers are related to operations on key events.
 */

export const onKeyBackUserPreferences = () => (dispatch: Function) => {
    dispatch(showMainSettingsScreen());
};

export const onKeyDownUserPreferences = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.USER_PREFERENCES]: { position },
        },
        userPreferences: { optionsList },
    } = getState();
    if (position < optionsList.length - 1) {
        dispatch(setSelectedUserPrefOption(optionsList[position + 1]));
        dispatch(onKeyDownList(ListTypes.USER_PREFERENCES));
    }
};

export const onKeyGuideUserPreferences = () => (dispatch: Function) => {
    dispatch(showGuideScreen());
};

export const onKeyOkUserPreferences = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.USER_PREFERENCES]: { position },
        },
        userPreferences: { optionsList },
    } = getState();
    const selectedItem = optionsList[position].title;
    switch (selectedItem) {
        case Locale.getString(LocaleKeys.SETTINGS_AUTOPLAY_NEXT_EPISODE):
            dispatch(showUserPrefAutoplayNextEpisodeScreen());
            break;

        case Locale.getString(LocaleKeys.SETTINGS_MENU_LANGUAGE):
            dispatch(showMenuLanguageScreen());
            break;

        case Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW):
            dispatch(showUserPrefVideoWindowScreen());
            break;

        case Locale.getString(LocaleKeys.SETTINGS_MY_SHOWS_OPTIONS):
            if (NetworkManager.isNetworkDisconnected()) {
                dispatch(
                    showWhisper({
                        message: Locale.getString(
                            LocaleKeys.SOMETHING_WENT_WRONG_WHISPER_DESCRIPTION,
                        ),
                        iconType: WhisperTypes.INFO,
                    }),
                );
                break;
            }
            dispatch(openMyShowsOptionsMenu());
            break;

        case Locale.getString(LocaleKeys.SETTINGS_ONEPASS_RECORDING_OPTIONS):
            if (NetworkManager.isNetworkDisconnected()) {
                dispatch(
                    showWhisper({
                        message: Locale.getString(
                            LocaleKeys.SOMETHING_WENT_WRONG_WHISPER_DESCRIPTION,
                        ),
                        iconType: WhisperTypes.INFO,
                    }),
                );
                break;
            }
            dispatch(showOnepassAndRecordingsMenu());
            break;

        default:
    }
};

export const onKeyUpUserPreferences = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.USER_PREFERENCES]: { position },
        },
        userPreferences: { optionsList },
    } = getState();
    if (position > 0) {
        dispatch(setSelectedUserPrefOption(optionsList[position - 1]));
        dispatch(onKeyUpList(ListTypes.USER_PREFERENCES));
    }
};

export const onMouseClickUserPreferences = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    if (value.includes(ListTypes.USER_PREFERENCES)) dispatch(onKeyOkUserPreferences());
};

export const onMouseOutUserPreferences = () => (dispatch: Function) => {
    dispatch(onMouseOutList());
};

export const onMouseOverUserPreferences = () => (dispatch: Function, getState: Function) => {
    dispatch(onMouseOverList());
    const {
        list: {
            [ListTypes.USER_PREFERENCES]: { position },
        },
        userPreferences: { optionsList },
    } = getState();
    dispatch(setSelectedUserPrefOption(optionsList[position]));
};

export default userPreferences;
